import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import { API_URL } from '../config';
import StepHeading from './StepHeading';
import { noAutofill } from '../noAutofill';

interface Story {
  id: string;
  title: string;
  style: string;
  script: string;
}

interface BuildCommercialStep6Props {
  commercialScript: string;
  setCommercialScript: (script: string) => void;
  loading: boolean;
  sessionId: string;
  /** The creative answers these scripts were written from. */
  creativeSignature: string;
  /** Exact cast the user wrote — sent with generation so the board cannot drop it. */
  castBrief?: string;
}

interface CachedStories {
  signature: string;
  stories: Story[];
}

const superpossibleKey = localStorage.getItem('superpossible_key');

const EMPTY_STORIES: Story[] = [
  { id: 'version1', title: 'Version 1', style: '', script: '' },
  { id: 'version2', title: 'Version 2', style: '', script: '' },
];

const BuildCommercialStep6: React.FC<BuildCommercialStep6Props> = ({ 
  commercialScript, 
  setCommercialScript, 
  loading,
  sessionId,
  creativeSignature,
  castBrief = '',
}) => {
  const [stories, setStories] = useState<Story[]>([
    { id: 'version1', title: 'Version 1', style: '', script: '' },
    { id: 'version2', title: 'Version 2', style: '', script: '' },
  ]);
  const [loadingStates, setLoadingStates] = useState<boolean[]>([false, false, false]);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [generationError, setGenerationError] = useState('');

  // API configuration is imported from config

  const storageKey = `commercial_stories_${sessionId}`;

  // Restore the previous run only when it was written from the same creative
  // answers. If the user went back and changed the product, style, cast, or
  // tone, the cached scripts no longer apply and we rewrite them.
  useEffect(() => {
    if (!sessionId) return;

    const saved = localStorage.getItem(storageKey);

    if (saved) {
      try {
        const cached: CachedStories = JSON.parse(saved);

        if (cached.signature === creativeSignature && Array.isArray(cached.stories)) {
          setStories(cached.stories);

          // Re-highlight whichever script the user had already chosen.
          const matchingIndex = commercialScript.trim()
            ? cached.stories.findIndex((story) => story.script === commercialScript)
            : -1;
          setSelectedIndex(matchingIndex);

          console.log('✅ Loaded persisted stories from localStorage');
          return;
        }
      } catch (error) {
        console.error('Error parsing saved stories:', error);
      }

      localStorage.removeItem(storageKey);
    }

    // The brief changed (or this is a first run) — start from a clean slate.
    setStories(EMPTY_STORIES);
    setSelectedIndex(-1);
    setCommercialScript('');
    generateStoriesProgressively();
  }, [sessionId, creativeSignature]);

  // Save stories to localStorage
  const saveStoriesToStorage = (updatedStories: Story[]) => {
    if (sessionId) {
      const payload: CachedStories = { signature: creativeSignature, stories: updatedStories };
      localStorage.setItem(storageKey, JSON.stringify(payload));
    }
  };

  const generateStoriesProgressively = async () => {
    if (!sessionId) return;
    
    setGenerationError('');
    
    // Start loading all three stories
    setLoadingStates([true, true, true]);
    
    try {
      console.log('🎬 Generating AI stories progressively for session:', sessionId);
      
      // Generate each story individually for progressive loading
      const storyPromises = EMPTY_STORIES.map(async (story, index) => {
        try {
          const response = await fetch(`${API_URL}/api/build-commercial/generate-single-story`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              session_id: sessionId,
              superpossible_key: superpossibleKey,
              story_type: story.id,
              cast_brief: castBrief,
            })
          });
          
          const result = await response.json();
          
          if (result.success && result.script) {
            // Update this specific story as soon as it's ready
            setStories(prevStories => {
              const newStories = [...prevStories];
              newStories[index] = { ...story, script: result.script };
              saveStoriesToStorage(newStories); // Save to localStorage
              return newStories;
            });
            
            // Don't auto-select any option - let user choose
            
            console.log(`✅ Generated ${story.title} story`);
          } else {
            throw new Error(result.error || `Failed to generate ${story.title} story`);
          }
          
        } catch (error) {
          console.error(`❌ Error generating ${story.title}:`, error);
          
          // Set fallback script for this story
          const fallbackScript = getFallbackScript(story.id);
          setStories(prevStories => {
            const newStories = [...prevStories];
            newStories[index] = { ...story, script: fallbackScript };
            saveStoriesToStorage(newStories); // Save to localStorage
            return newStories;
          });
          
          // Don't auto-select any option - let user choose
        } finally {
          // Mark this story as done loading
          setLoadingStates(prevStates => {
            const newStates = [...prevStates];
            newStates[index] = false;
            return newStates;
          });
        }
      });
      
      // Wait for all stories to complete
      await Promise.all(storyPromises);
      
    } catch (error) {
      console.error('❌ Story generation error:', error);
      setGenerationError('Failed to generate AI stories. Using fallback scripts.');
      
      // Load fallback scripts
      const fallbackStories = EMPTY_STORIES.map(story => ({
        ...story,
        script: getFallbackScript(story.id)
      }));
      setStories(fallbackStories);
      saveStoriesToStorage(fallbackStories); // Save fallback stories to localStorage
      // Don't auto-select any option - let user choose
      setLoadingStates([false, false, false]);
    }
  };

  const getFallbackScript = (storyId: string): string => {
    const fallbacks: Record<string, string> = {
      version1: `Scene: Kitchen at 7am, rain on the glass, one mug already cold
0-2s: She sets the mug down harder than she meant to
2-5s: The old routine fails again. She almost laughs.
5-8s: One touch. The product works. She actually pauses.
8-10s: Product in her hands, steam catching the window light
Line 1: "No. Not again."
Line 2: "Okay. That's new."`,
      version2: `Scene: Late apartment, city glow, a quiet table
0-2s: He is already mid-reach, looking for a better way
2-5s: The usual fix is not enough and he knows it
5-8s: The product lands in frame and solves it without a speech
8-10s: Still hold. Product, hands, one honest breath
Line 1: "Wait. It actually works?"
Line 2: "Told you. Watch again."`,
      energetic: `Scene: Kitchen at 7am, rain on the glass, one mug already cold
0-2s: She sets the mug down harder than she meant to
2-5s: The old routine fails again. She almost laughs.
5-8s: One touch. The product works. She actually pauses.
8-10s: Product in her hands, steam catching the window light
Line 1: "No. Not again."
Line 2: "Okay. That's new."`,
      warm: `Scene: Late apartment, city glow, a quiet table
0-2s: He is already mid-reach, looking for a better way
2-5s: The usual fix is not enough and he knows it
5-8s: The product lands in frame and solves it without a speech
8-10s: Still hold. Product, hands, one honest breath
Line 1: "Wait. It actually works?"
Line 2: "Told you. Watch again."`,
    };
    return fallbacks[storyId] || fallbacks.version1;
  };

  const handleStorySelect = (index: number) => {
    setSelectedIndex(index);
    setCommercialScript(stories[index].script);
  };

  const handleStoryEdit = (index: number, newScript: string) => {
    // Update the story in the local state
    setStories(prevStories => {
      const newStories = [...prevStories];
      newStories[index] = { ...newStories[index], script: newScript };
      saveStoriesToStorage(newStories); // Save changes to localStorage
      return newStories;
    });
    
    // If this is the selected story, update the commercial script
    if (index === selectedIndex) {
      setCommercialScript(newScript);
    }
  };

  return (
    <div className="ed-enter">
      <StepHeading
        index={6}
        kicker="The storyboard"
        title="The writers' room delivered two."
        lede="Two ten-second boards. Choose the one you would actually watch, then rewrite any beat straight on the page."
      />

      {generationError && (
        <div className="ed-alert" role="alert" style={{ marginBottom: 24 }}>
          <span>{generationError}</span>
          <button type="button" className="ed-alert-action" onClick={generateStoriesProgressively}>
            Try again
          </button>
        </div>
      )}

      <div className="ed-block">
        <div className="ed-docs" role="radiogroup" aria-label="Commercial scripts">
          {stories.map((story, index) => {
            const isSelected = selectedIndex === index;

            if (loadingStates[index]) {
              return (
                <div key={story.id} className="ed-skeleton ed-skeleton--tall">
                  Storyboarding {story.title}
                </div>
              );
            }

            return (
              <div
                key={story.id}
                role="radio"
                aria-checked={isSelected}
                tabIndex={0}
                className={`ed-doc${isSelected ? ' is-selected' : ''}`}
                onClick={() => handleStorySelect(index)}
                onKeyDown={(e) => {
                  if ((e.target as HTMLElement).tagName === 'TEXTAREA') return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleStorySelect(index);
                  }
                }}
              >
                {isSelected && (
                  <span className="ed-mark" aria-hidden="true">
                    <Check />
                  </span>
                )}

                <div className="ed-doc-head">
                  <span className="ed-doc-label">
                    {String(index + 1).padStart(2, '0')} — {story.title}
                  </span>
                  {story.style && <span className="ed-tag">{story.style}</span>}
                </div>

                <textarea
                  className="ed-doc-script"
                  value={story.script}
                  onChange={(e) => handleStoryEdit(index, e.target.value)}
                  onFocus={() => handleStorySelect(index)}
                  placeholder={`${story.title} script will appear here…`}
                  aria-label={`${story.title} script`}
                  {...noAutofill}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BuildCommercialStep6;
